import { defineStore } from "pinia";
import { ref } from "vue";
import PayrollOvertimeEntryService from "@/services/payrollOvertimeEntry.service";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const usePayrollOvertimeEntryStore = defineStore(
  "payrollOvertimeEntry",
  () => {
    const entries = ref([]);
    const employees = ref([]);
    const overtimeDefinitions = ref([]);
    const loading = ref(false);
    const isLoading = ref(false);
    const error = ref(null);
    const lastFilters = ref({});

    const fetchAll = async (filters = {}) => {
      if (!(await guardPlanModule(MODULE_KEYS.HRM_PAYROLL))) {
        entries.value = [];
        return;
      }

      loading.value = true;
      lastFilters.value = { ...filters };
      try {
        entries.value = await PayrollOvertimeEntryService.getAll(filters);
        error.value = null;
      } catch (err) {
        error.value =
          err?.response?.data?.message ||
          "Failed to load employee overtime entries.";
      } finally {
        loading.value = false;
      }
    };

    const fetchDependencies = async () => {
      try {
        const [employeeRows, overtimeRows] = await Promise.all([
          PayrollOvertimeEntryService.getEmployees({
            status: "Active",
            per_page: 5000,
          }),
          PayrollOvertimeEntryService.getOvertimeDefinitions(),
        ]);

        employees.value = (employeeRows || []).filter(
          (employee) =>
            String(employee?.status || "").trim().toLowerCase() === "active",
        );
        overtimeDefinitions.value = overtimeRows;
      } catch (err) {
        error.value =
          err?.response?.data?.message ||
          "Failed to load overtime entry form dependencies.";
      }
    };

    const refresh = async () => {
      await fetchAll(lastFilters.value || {});
    };

    const createEntry = async (payload) => {
      isLoading.value = true;
      try {
        await PayrollOvertimeEntryService.create(payload);
        toast("Overtime entry created successfully!");
        await refresh();
      } catch (err) {
        error.value =
          err?.response?.data?.message || "Failed to create overtime entry.";
        toast(error.value, "error");
        throw err;
      } finally {
        isLoading.value = false;
      }
    };

    const updateEntry = async (id, payload) => {
      isLoading.value = true;
      try {
        await PayrollOvertimeEntryService.update(id, payload);
        toast("Overtime entry updated successfully!");
        await refresh();
      } catch (err) {
        error.value =
          err?.response?.data?.message || "Failed to update overtime entry.";
        toast(error.value, "error");
        throw err;
      } finally {
        isLoading.value = false;
      }
    };

    const deleteEntry = async (id) => {
      isLoading.value = true;
      try {
        await PayrollOvertimeEntryService.delete(id);
        toast("Overtime entry deleted successfully!");
        await refresh();
      } catch (err) {
        error.value =
          err?.response?.data?.message || "Failed to delete overtime entry.";
        toast(error.value, "error");
        throw err;
      } finally {
        isLoading.value = false;
      }
    };

    return {
      entries,
      employees,
      overtimeDefinitions,
      loading,
      isLoading,
      error,
      fetchAll,
      fetchDependencies,
      refresh,
      createEntry,
      updateEntry,
      deleteEntry,
    };
  },
);
